import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import "~/css/KnowledgeDetailModal.css";
import { talkModes } from "~/Commons";
import { useAtom } from "jotai/index";
import { standbyModeAtom } from "~/Atom";
import { useTranslation } from "react-i18next";

type KnowledgeDetailModalProps = {
  title: string;
  content: string;
  isOpen: boolean;
  onClose: () => void;
  isLoading?: boolean;
  error?: string | null;
};

const KnowledgeDetailModal: React.FC<KnowledgeDetailModalProps> = ({
  title,
  content,
  isOpen,
  onClose,
  isLoading = false,
  error = null,
  // eslint-disable-next-line complexity
}) => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [animationClass, setAnimationClass] = useState("");
  const [, setStandbyMode] = useAtom(standbyModeAtom);

  useEffect(() => {
    if (isOpen) {
      setStandbyMode(talkModes.stopped);
      setIsVisible(true);
      setAnimationClass("animate-in");
      return () => {};
    } else {
      setAnimationClass("animate-out");
      // アニメーション終わった後に非表示
      const timer = setTimeout(() => setIsVisible(false), 400);
      return () => clearTimeout(timer);
    }
  }, [isOpen, setStandbyMode]);

  if (!isVisible) return null;

  // 文字数を計算
  const characterCount = content ? content.length : 0;

  const knowledgeDetailModalContent = (
    <div className="knowledge-detail-modal-overlay">
      <div className={`knowledge-detail-modal-base ${animationClass}`}>
        {/* タイトル */}
        <div className="knowledge-detail-modal-header">
          <h2 className="font-default-bold-24">{title}</h2>
        </div>

        {/* コンテンツエリア */}
        <div className="knowledge-detail-modal-content">
          {isLoading && (
            <div className="knowledge-detail-modal-loading">
              <div>{t("loading")}</div>
            </div>
          )}

          {error && !isLoading && (
            <div className="knowledge-detail-modal-error">
              <strong>{t("error")}:</strong> {error}
            </div>
          )}

          {content && !isLoading && !error && (
            <div className="knowledge-detail-modal-text-area">
              <pre>{content}</pre>
            </div>
          )}
        </div>

        {/* 文字数表示（コンテンツエリアの外） */}
        {content && !isLoading && !error && (
          <div className="knowledge-detail-modal-char-count">
            {t("characterCount")}：{characterCount}
          </div>
        )}

        {/* 閉じるボタン */}
        <div className="knowledge-detail-modal-footer">
          <button className="knowledge-detail-modal-close-button" onClick={onClose}>
            {t("close")}
          </button>
        </div>
      </div>
    </div>
  );

  // ポータルを使ってdocument.bodyに直接レンダリング
  return createPortal(knowledgeDetailModalContent, document.body);
};

export default KnowledgeDetailModal;
